/**
 * API client for the existing Z2Market Express backend.
 *
 * Auth: the session lives in the httpOnly `z2_access_token` cookie, which
 * `authenticate.ts` reads *before* the Authorization header. Same-origin fetch
 * sends it automatically, so there is no token plumbing here at all.
 *
 * Every endpoint answers with `{ ok: true, data }` or `{ ok: false, error }`.
 */
export class ApiError extends Error {
  constructor(message, { status, code, details, retryAfterMs } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    /** How long the server asked us to wait, when it said so (429). */
    this.retryAfterMs = retryAfterMs ?? null;
  }
}

/**
 * The wait the server advertised, in milliseconds.
 *
 * The chat limiter (express-rate-limit) answers 429 with both `Retry-After` and
 * the draft-6 `RateLimit-Reset`, each in seconds. They are read here so a caller
 * can wait the window out instead of guessing.
 */
function readRetryAfterMs(response) {
  for (const header of ['retry-after', 'ratelimit-reset']) {
    const value = response.headers.get(header);
    if (value === null) continue;
    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds * 1000);
  }
  return null;
}

export async function apiFetch(path, { method = 'GET', body, raw, signal } = {}) {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: raw ? undefined : body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: raw ?? (body !== undefined ? JSON.stringify(body) : undefined),
    signal,
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    /* 204 or non-JSON body */
  }

  if (!response.ok || payload?.ok === false) {
    throw new ApiError(payload?.error?.message ?? `Request failed (${response.status})`, {
      status: response.status,
      code: payload?.error?.code,
      details: payload?.error?.details,
      retryAfterMs: readRetryAfterMs(response),
    });
  }

  return payload?.data ?? payload;
}

/** Multipart upload. The field name is fixed by the server's multer config. */
export function uploadChatImage(file, { signal } = {}) {
  const form = new FormData();
  form.append('image', file, file.name);
  return apiFetch('/api/upload/chat-image', { method: 'POST', raw: form, signal });
}

export const api = {
  get: (path, options) => apiFetch(path, options),
  post: (path, body, options) => apiFetch(path, { ...options, method: 'POST', body }),
};
